const COOKIE_NAME = 'ml_consent'
const LEGACY_STORAGE_KEY = 'ml-consent'
const POLICY_VERSION = '2026-11-05'
const MAX_AGE_DAYS = 180
const GA_ID = 'G-D429LPHJH7'
const PRODUCTION_HOST = /(^|\.)michelelaurelli\.it$/

const COPY = {
    it: {
        locale: 'it-IT',
        cookiePolicy: '/cookie/',
        title: 'Un biscotto, solo se ti va',
        text: 'Il sito usa solo i cookie tecnici che gli servono per funzionare. Se mi dai il permesso attivo anche Google Analytics, che mi dice in forma aggregata quante persone passano di qui e cosa leggono. Niente pubblicità, niente profilazione. I dettagli sono nella',
        policyLink: 'cookie policy',
        reject: 'Rifiuta',
        accept: 'Accetta',
        customize: 'Scegli tu cosa attivare',
        prefsTitle: 'Preferenze cookie',
        prefsText: 'Decidi tu cosa attivare. La scelta vale anche per il blog e per la pagina del libro, e puoi cambiarla quando vuoi da «Preferenze cookie», in fondo a ogni pagina.',
        technical: 'Tecnici',
        always: 'Sempre attivi',
        technicalText: 'Ricordano le tue scelte sui cookie e sull\'audio. Restano nel tuo browser e non vengono condivisi con nessuno.',
        analytics: 'Statistiche',
        analyticsText: 'Google Analytics 4 di Google Ireland Ltd. Conta visite e sezioni lette in forma aggregata, senza segnali pubblicitari. Cookie <code>_ga</code> e <code>_ga_D429LPHJH7</code>, durata fino a 2 anni.',
        rejectAll: 'Rifiuta tutti',
        save: 'Salva le scelte',
        policyFull: 'Cookie policy completa',
        back: 'Indietro',
        close: 'Chiudi',
        record: (date, code) => `Scelta registrata il ${date}, codice ${code}`,
    },
    en: {
        locale: 'en-US',
        cookiePolicy: '/en/cookies/',
        title: 'A cookie, only if you like',
        text: 'This site only uses the technical cookies it needs to work. If you allow it, I also turn on Google Analytics, which tells me in aggregate how many people stop by and what they read. No ads, no profiling. Details are in the',
        policyLink: 'cookie policy',
        reject: 'Decline',
        accept: 'Accept',
        customize: 'Choose what to enable',
        prefsTitle: 'Cookie preferences',
        prefsText: 'You decide what to enable. Your choice also applies to the blog and the book page, and you can change it any time from «Cookie preferences» at the bottom of every page.',
        technical: 'Strictly necessary',
        always: 'Always on',
        technicalText: 'They remember your cookie and audio choices. They stay in your browser and are never shared.',
        analytics: 'Analytics',
        analyticsText: 'Google Analytics 4 by Google Ireland Ltd. Counts visits and sections read in aggregate, with no advertising signals. Cookies <code>_ga</code> and <code>_ga_D429LPHJH7</code>, lasting up to 2 years.',
        rejectAll: 'Decline all',
        save: 'Save choices',
        policyFull: 'Full cookie policy',
        back: 'Back',
        close: 'Close',
        record: (date, code) => `Choice recorded on ${date}, code ${code}`,
    },
}

const CLOSE_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" /></svg>'

const COOKIE_ICON = `<svg class="consent-cookie" viewBox="0 0 64 64" aria-hidden="true">
    <defs>
        <radialGradient id="consent-cookie-fill" cx="38%" cy="34%" r="70%"><stop offset="0" stop-color="#F6CF8E" /><stop offset="1" stop-color="#E0A458" /></radialGradient>
        <mask id="consent-cookie-bite"><rect width="64" height="64" fill="white" /><circle cx="56" cy="10" r="11" fill="black" /><circle cx="61" cy="24" r="6" fill="black" /></mask>
    </defs>
    <g mask="url(#consent-cookie-bite)"><circle cx="32" cy="34" r="27" fill="#C9853C" /><circle cx="32" cy="32" r="27" fill="url(#consent-cookie-fill)" /></g>
    <circle cx="22" cy="24" r="3.6" fill="#5B3A22" /><circle cx="35" cy="40" r="4.2" fill="#5B3A22" /><circle cx="19" cy="42" r="3" fill="#5B3A22" />
    <circle cx="42" cy="26" r="2.6" fill="#5B3A22" /><circle cx="30" cy="16" r="2" fill="#5B3A22" /><circle cx="46" cy="44" r="2.2" fill="#5B3A22" />
</svg>`

export default class Consent {

    /**
     * @param {object} options
     * @param {boolean} options.waitForIntro Show the banner only after the 'intro-finished' event
     * @param {Function} options.beforeOpen Called before the preferences open from a trigger
     * @param {boolean} options.newTabLinks Open the cookie policy in a new tab
     */
    constructor({ waitForIntro = false, beforeOpen = null, newTabLinks = false } = {}) {
        this.lang = document.documentElement.lang === 'en' ? 'en' : 'it'
        this.copy = COPY[this.lang]
        this.beforeOpen = beforeOpen
        this.linkTarget = newTabLinks ? ' target="_blank" rel="noopener"' : ''
        this.analyticsLoaded = false

        this.render()
        this.state = this.read()

        if (this.state) {
            this.apply(this.state)
        } else if (waitForIntro) {
            this.waitForIntro()
        } else {
            this.fallbackTimeout = setTimeout(() => !this.state && this.show('main'), 700)
        }

        this.addEventListeners()
        this.announce()
    }

    /**Markup*/
    render() {
        const c = this.copy
        const root = document.createElement('section')
        root.id = 'consent'
        root.className = 'consent'
        root.hidden = true
        root.setAttribute('role', 'dialog')
        root.setAttribute('aria-modal', 'false')
        root.innerHTML = `
            <div id="consent-main" class="consent-view">
                <div class="consent-head">
                    ${COOKIE_ICON}
                    <h2 id="consent-title" class="consent-title">${c.title}</h2>
                </div>
                <p id="consent-text" class="consent-text">${c.text} <a class="consent-link" href="${c.cookiePolicy}"${this.linkTarget}>${c.policyLink}</a>.</p>
                <div class="consent-actions">
                    <button type="button" class="consent-button" data-consent="reject">${c.reject}</button>
                    <button type="button" class="consent-button consent-button-primary" data-consent="accept">${c.accept}</button>
                </div>
                <button type="button" class="consent-more" data-consent="customize">${c.customize}</button>
            </div>
            <div id="consent-prefs" class="consent-view" hidden>
                <div class="consent-head consent-head-prefs">
                    <h2 id="consent-prefs-title" class="consent-title">${c.prefsTitle}</h2>
                    <button type="button" class="consent-close" data-consent="back" aria-label="${c.back}">${CLOSE_ICON}</button>
                </div>
                <p id="consent-prefs-text" class="consent-text">${c.prefsText}</p>
                <ul class="consent-categories">
                    <li class="consent-category">
                        <div class="consent-category-head">
                            <span class="consent-category-name">${c.technical}</span>
                            <span class="consent-always">${c.always}</span>
                        </div>
                        <p>${c.technicalText}</p>
                    </li>
                    <li class="consent-category">
                        <div class="consent-category-head">
                            <label class="consent-category-name" for="consent-analytics">${c.analytics}</label>
                            <input type="checkbox" id="consent-analytics" class="consent-switch" role="switch" aria-describedby="consent-analytics-text">
                        </div>
                        <p id="consent-analytics-text">${c.analyticsText}</p>
                    </li>
                </ul>
                <div class="consent-actions">
                    <button type="button" class="consent-button" data-consent="reject">${c.rejectAll}</button>
                    <button type="button" class="consent-button consent-button-primary" data-consent="save">${c.save}</button>
                </div>
                <p class="consent-foot">
                    <a class="consent-link" href="${c.cookiePolicy}"${this.linkTarget}>${c.policyFull}</a>
                    <span id="consent-record" hidden></span>
                </p>
            </div>`

        document.body.appendChild(root)

        this.domElements = {
            root,
            mainView: root.querySelector('#consent-main'),
            prefsView: root.querySelector('#consent-prefs'),
            analyticsSwitch: root.querySelector('#consent-analytics'),
            record: root.querySelector('#consent-record'),
            backButton: root.querySelector('[data-consent="back"]'),
        }
    }

    /**Storage*/
    read() {
        const state = this.readCookie() || this.readLegacy()
        if (!state || state.version !== POLICY_VERSION) return null

        const age = Date.now() - new Date(state.date).getTime()
        if (!(age >= 0 && age < MAX_AGE_DAYS * 864e5)) return null

        return state
    }

    readCookie() {
        const entry = document.cookie.split('; ').find((cookie) => cookie.startsWith(`${COOKIE_NAME}=`))
        if (!entry) return null

        try {
            return JSON.parse(decodeURIComponent(entry.slice(COOKIE_NAME.length + 1)))
        } catch (error) {
            return null
        }
    }

    readLegacy() {
        try {
            const state = JSON.parse(localStorage.getItem(LEGACY_STORAGE_KEY))
            localStorage.removeItem(LEGACY_STORAGE_KEY)
            if (state) this.writeCookie(state)
            return state
        } catch (error) {
            return null
        }
    }

    write(analytics) {
        const state = {
            id: this.state?.id || this.createId(),
            version: POLICY_VERSION,
            date: new Date().toISOString(),
            analytics,
        }

        this.writeCookie(state)

        return state
    }

    writeCookie(state) {
        const host = window.location.hostname
        const domain = PRODUCTION_HOST.test(host) ? '; Domain=michelelaurelli.it' : ''
        const secure = window.location.protocol === 'https:' ? '; Secure' : ''

        document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(state))}; Path=/; Max-Age=${MAX_AGE_DAYS * 86400}; SameSite=Lax${domain}${secure}`
    }

    createId() {
        if (window.crypto?.randomUUID) return window.crypto.randomUUID()

        return Date.now().toString(36) + Math.random().toString(36).slice(2, 10)
    }

    /**Choices*/
    choose(analytics) {
        const changed = !this.state || this.state.analytics !== analytics

        this.state = this.write(analytics)
        if (changed) this.apply(this.state)

        this.announce()
        this.hide()
    }

    announce() {
        window.dispatchEvent(new CustomEvent('consent-change', { detail: this.state }))
    }

    apply(state) {
        if (state.analytics) {
            this.enableAnalytics()
        } else {
            this.disableAnalytics()
        }
    }

    enableAnalytics() {
        window[`ga-disable-${GA_ID}`] = false
        this.gtag('consent', 'update', { analytics_storage: 'granted' })

        if (this.analyticsLoaded || !PRODUCTION_HOST.test(window.location.hostname)) return
        this.analyticsLoaded = true

        const script = document.createElement('script')
        script.async = true
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`
        document.head.appendChild(script)

        this.gtag('js', new Date())
        this.gtag('config', GA_ID, {
            allow_google_signals: false,
            allow_ad_personalization_signals: false,
        })
    }

    disableAnalytics() {
        this.gtag('consent', 'update', { analytics_storage: 'denied' })
        window[`ga-disable-${GA_ID}`] = true

        this.deleteAnalyticsCookies()
    }

    deleteAnalyticsCookies() {
        const names = document.cookie
            .split(';')
            .map((cookie) => cookie.split('=')[0].trim())
            .filter((name) => name === '_ga' || name.startsWith('_ga_') || name === '_gid' || name === '_gat')

        const host = window.location.hostname
        const domains = ['', host, `.${host}`, '.michelelaurelli.it']

        names.forEach((name) => {
            domains.forEach((domain) => {
                document.cookie = `${name}=; Max-Age=0; path=/${domain ? `; domain=${domain}` : ''}; SameSite=Lax`
            })
        })
    }

    gtag() {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push(arguments)
    }

    /**Banner*/
    waitForIntro() {
        const show = () => {
            window.removeEventListener('intro-finished', onIntro)
            clearTimeout(this.fallbackTimeout)
            if (!this.state) this.show('main')
        }
        const onIntro = () => setTimeout(show, 900)

        window.addEventListener('intro-finished', onIntro)
        this.fallbackTimeout = setTimeout(show, 12000)
    }

    show(view, trigger = null) {
        const { root, analyticsSwitch, backButton } = this.domElements

        clearTimeout(this.fallbackTimeout)
        this.returnFocus = trigger
        this.reopened = view === 'prefs' && !!this.state
        root.classList.toggle('consent-reopened', this.reopened)
        backButton.setAttribute('aria-label', this.reopened ? this.copy.close : this.copy.back)

        analyticsSwitch.checked = !!this.state?.analytics
        this.updateRecord()
        this.switchView(view)

        root.hidden = false
        requestAnimationFrame(() => requestAnimationFrame(() => root.classList.add('consent-visible')))

        if (trigger) setTimeout(() => this.focusFirst(), 50)
    }

    hide() {
        const { root } = this.domElements
        if (root.hidden) return

        root.classList.remove('consent-visible')
        clearTimeout(this.hideTimeout)
        this.hideTimeout = setTimeout(() => {
            if (!root.classList.contains('consent-visible')) root.hidden = true
        }, 450)

        if (this.returnFocus && document.contains(this.returnFocus)) this.returnFocus.focus({ preventScroll: true })
        this.returnFocus = null
    }

    switchView(view) {
        const { root, mainView, prefsView } = this.domElements

        mainView.hidden = view !== 'main'
        prefsView.hidden = view !== 'prefs'
        root.setAttribute('aria-labelledby', view === 'main' ? 'consent-title' : 'consent-prefs-title')
        root.setAttribute('aria-describedby', view === 'main' ? 'consent-text' : 'consent-prefs-text')
        root.dataset.view = view
    }

    focusFirst() {
        const view = this.domElements.root.dataset.view === 'main' ? this.domElements.mainView : this.domElements.prefsView
        const target = view.querySelector('input, button')
        if (target) target.focus({ preventScroll: true })
    }

    updateRecord() {
        const { record } = this.domElements
        if (!this.state) {
            record.hidden = true
            return
        }

        const date = new Date(this.state.date).toLocaleDateString(this.copy.locale, { day: 'numeric', month: 'long', year: 'numeric' })
        record.textContent = this.copy.record(date, this.state.id.slice(0, 8))
        record.hidden = false
    }

    open(trigger) {
        if (this.beforeOpen) this.beforeOpen(trigger)

        const dialog = trigger?.closest('dialog[open]')
        if (dialog) dialog.close()

        this.show('prefs', trigger)
    }

    /**Events*/
    addEventListeners() {
        const { root, analyticsSwitch } = this.domElements

        root.addEventListener('click', (event) => {
            event.stopPropagation()

            const action = event.target.closest('[data-consent]')?.dataset.consent
            if (action === 'accept') this.choose(true)
            if (action === 'reject') this.choose(false)
            if (action === 'save') this.choose(analyticsSwitch.checked)
            if (action === 'customize') {
                this.switchView('prefs')
                this.focusFirst()
            }
            if (action === 'back') {
                if (this.reopened) {
                    this.hide()
                } else {
                    this.switchView('main')
                    this.focusFirst()
                }
            }
        })

        //Keep wheel, touch and keys inside the banner from moving the page sections
        ;['wheel', 'mousewheel', 'touchstart', 'touchend', 'keydown'].forEach((type) => {
            root.addEventListener(type, (event) => event.stopPropagation())
        })

        root.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && this.reopened) this.hide()
        })

        document.addEventListener('click', (event) => {
            const trigger = event.target.closest('[data-consent-open]')
            if (!trigger) return

            event.preventDefault()
            event.stopPropagation()
            this.open(trigger)
        }, true)
    }
}
